import { IParagraphOptions, Paragraph, TextRun } from 'docx'
import { DraftTextHighlighterItem } from '../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  EntryType,
  QuestionSettingCode,
  QuestionTypeCode,
  SettingValue
} from '../../data/model/questionnaire'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import {
  getQuestionEntryPrefix,
  getQuestionSettings,
  getQuestionTimerLabel,
  getQuestionType
} from '../questionnaireUtils'
import { formatHiddenMarkets } from './createHiddenMarketsLine'
import { QUESTION_TYPE_CONJOINT, QUESTION_TYPE_MAXDIFF } from './model'
import { paragraphTopBorderOptions } from './paragraphTopBorderOptions'

const getQuestionTypeText = (
  entry: DraftQuestionnaireEntry,
  questionType: QuestionTypeCode
): string => {
  if (entry.entryType === EntryType.SectionEntryType) {
    return '(Section - start)'
  }

  switch (questionType) {
    case QuestionTypeCode.CustomAudience:
    case QuestionTypeCode.StandardAudience:
    case QuestionTypeCode.Basic: {
      const entryItem = entry.entryItem as DraftQuestionItem
      const { settingValues } = entryItem
      const questionSettings = getQuestionSettings(settingValues)

      const questionTypeText = `(${
        questionSettings.get(
          questionType === QuestionTypeCode.CustomAudience
            ? QuestionSettingCode.CustomAudienceChoice
            : QuestionSettingCode.BasicChoice
        ) === SettingValue.MultipleChoice
          ? 'Multi'
          : 'Single'
      } select)`
      return questionTypeText
    }
    case QuestionTypeCode.TextCard:
      return '(Text instruction)'
    case QuestionTypeCode.Matrix: {
      const entryItem = entry.entryItem as DraftMatrixItem
      const { settingValues } = entryItem
      const questionSettings = getQuestionSettings(settingValues)

      return `(Matrix - ${
        questionSettings.get(QuestionSettingCode.MatrixChoice) ===
        SettingValue.MultipleChoice
          ? 'multi'
          : 'single'
      } select)`
    }
    case QuestionTypeCode.Scale:
      return '(Slider)'
    case QuestionTypeCode.Ranked: {
      const entryItem = entry.entryItem as DraftQuestionItem
      const { settingValues } = entryItem
      const questionSettings = getQuestionSettings(settingValues)

      const choiceRequisite = questionSettings.get(
        QuestionSettingCode.RankChoiceRequisite
      )

      const rankedQuestionText = `(Ranked)${
        choiceRequisite ? ` [Rank top ${choiceRequisite}]` : ' [All ranked]'
      }`
      return rankedQuestionText
    }
    case QuestionTypeCode.FreeText:
      return '(Text input)'
    case QuestionTypeCode.TextHighlighter:
      const entryItem = entry.entryItem as DraftTextHighlighterItem
      const { settingValues } = entryItem
      const questionSettings = getQuestionSettings(settingValues)

      return `(Text highlighter - ${
        questionSettings.get(QuestionSettingCode.BasicChoice) ===
        SettingValue.MultipleChoice
          ? 'multi'
          : 'single'
      } select)`
    case QuestionTypeCode.MaxDiff:
      return `(${QUESTION_TYPE_MAXDIFF})`
    case QuestionTypeCode.Conjoint:
      return `(${QUESTION_TYPE_CONJOINT})`
    default:
      return ''
  }
}

const createQuestionParagraphOptions = (
  entry: DraftQuestionnaireEntry,
  allMarkets: MarketOption[]
): IParagraphOptions => {
  const questionType = getQuestionType(entry)
  const questionPrefixAndNumber = getQuestionEntryPrefix(entry)
  const questionTimerLabel = getQuestionTimerLabel(entry)

  // Entry-level market visibility is appended to the header line (rather than a
  // separate line) so it can't be mistaken for a response option / matrix row /
  // text-card subtitle when the .docx is re-imported.
  const hiddenMarkets = formatHiddenMarkets(allMarkets, entry.hiddenInMarkets)

  return {
    children: [
      new TextRun({
        text: `${questionPrefixAndNumber} `,
        bold: true
      }),
      new TextRun({
        text: `${getQuestionTypeText(
          entry,
          questionType
        )} ${questionTimerLabel}`
      }),
      ...(hiddenMarkets
        ? [
            new TextRun({
              text: `  ${hiddenMarkets}`,
              italics: true,
              color: '808080'
            })
          ]
        : [])
    ]
  }
}

export const createQuestionTitle = ({
  entry,
  shouldHaveTopBorder,
  allMarkets = []
}: {
  entry: DraftQuestionnaireEntry
  shouldHaveTopBorder: boolean
  allMarkets?: MarketOption[]
}): Paragraph => {
  const questionParagraphOptions = createQuestionParagraphOptions(
    entry,
    allMarkets
  )

  return new Paragraph(
    shouldHaveTopBorder
      ? { ...questionParagraphOptions, ...paragraphTopBorderOptions }
      : questionParagraphOptions
  )
}
